import axios from "axios";
import { toast } from "sonner";
import { readJsonPreference, transientSession, writeJsonPreference } from "@/utils/preferences";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HUB_TOPICS = [
  "Metabolic Health", "Hormones", "Energy & Mitochondria", "Sleep", "Stress",
  "Gut Health", "Heart Health", "Immunity", "Brain Health", "Movement & Recovery", "Nutrition",
];

export const HUB_TYPES = [
  { key: "all", label: "All" },
  { key: "article", label: "Articles" },
  { key: "video", label: "Videos" },
  { key: "youtube", label: "YouTube" },
  { key: "guide", label: "Guides" },
  { key: "event", label: "Events" },
];

export const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/);
  return m ? m[1] : null;
};

const BOOKMARKS_KEY = "iwmc_hub_bookmarks";
const RECENT_KEY = "iwmc_hub_recent";

// Non-sensitive per-tab hint only. Authentication remains in an httpOnly cookie.
const MEMBER_FLAG = "iwc_member_hint";
export const setMemberFlag = (on) => {
  if (on) transientSession.set(MEMBER_FLAG, "1");
  else transientSession.remove(MEMBER_FLAG);
};
export const hasMemberFlag = () => transientSession.get(MEMBER_FLAG) === "1";

export const getHubBookmarks = () => {
  const bookmarks = readJsonPreference(BOOKMARKS_KEY, []);
  return Array.isArray(bookmarks) ? bookmarks : [];
};
export const isHubBookmarked = (id) => getHubBookmarks().includes(id);
export const toggleHubBookmark = (id) => {
  const list = getHubBookmarks();
  const saved = list.includes(id);
  const next = saved ? list.filter((x) => x !== id) : [...list, id];
  writeJsonPreference(BOOKMARKS_KEY, next);
  if (transientSession.get(MEMBER_FLAG)) {
    axios.post(`${API}/hub/bookmarks/${id}`, {}, { withCredentials: true }).catch(() => {});
  } else if (!saved) {
    toast("Saved on this device", { description: "Sign in to sync your reading list across devices." });
  }
  window.dispatchEvent(new Event("hub-bookmarks-changed"));
  return !saved;
};

let syncInFlight = null;
export const syncHubBookmarks = () => {
  if (syncInFlight) return syncInFlight;
  syncInFlight = axios.post(`${API}/hub/bookmarks/merge`, { ids: getHubBookmarks() }, { withCredentials: true })
    .then(({ data }) => {
      writeJsonPreference(BOOKMARKS_KEY, data.ids || []);
      window.dispatchEvent(new Event("hub-bookmarks-changed"));
    })
    .catch(() => { /* not signed in — browser bookmarks only */ })
    .finally(() => { setTimeout(() => { syncInFlight = null; }, 2000); });
  return syncInFlight;
};

export const getHubRecent = () => {
  const recent = readJsonPreference(RECENT_KEY, []);
  return Array.isArray(recent) ? recent : [];
};
export const pushHubRecent = (slug) => {
  const list = getHubRecent().filter((s) => s !== slug);
  list.unshift(slug);
  writeJsonPreference(RECENT_KEY, list.slice(0, 6));
};

export const trackHubEvent = (eventType, contentId = null, meta = null) => {
  axios.post(`${API}/hub/events`, { eventType, contentId, meta }).catch(() => {});
};

export const markHubRead = (id) => {
  if (!transientSession.get(MEMBER_FLAG)) return;
  axios.post(`${API}/hub/reads/${id}`, {}, { withCredentials: true })
    .then(({ data }) => {
      (data.newBadges || []).forEach((b) => {
        toast.success(`Badge earned: ${b.label}!`, { description: "Find it with your badges on the Challenges page." });
      });
    })
    .catch(() => {});
};

export const VANTYX_LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_d50f8085-4875-4de7-b3cb-affb13e5c279/artifacts/xapigwue_Vantyx.jpeg";
export const BIOHACK_LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_d50f8085-4875-4de7-b3cb-affb13e5c279/artifacts/4yilmmh4_BIOHACK%20R%20LOGO-02.webp";

export const SITE_SPONSORS = [
  { name: "Vantyx", tagline: "Premium supplement partner supporting the Wellness Hub.", logo: VANTYX_LOGO, logoBg: "bg-white border border-slate-200", imgClass: "h-full w-auto object-contain rounded-lg shadow-md", url: "https://vantyxwellness.com" },
  { name: "Biohack Yourself", tagline: "Biohacking Health: Latest News & Insights — contributing stories, interviews, podcasts & announcements.", logo: BIOHACK_LOGO, logoBg: "bg-navy", url: "https://www.biohackyourself.com" },
];

export const TOPIC_NEXT_STEP = {
  "Metabolic Health": { label: "Explore metabolic-friendly venues", to: "/?domain=D1%20Metabolic" },
  "Hormones": { label: "Explore hormone-supportive venues", to: "/?domain=D2%20Hormonal" },
  "Energy & Mitochondria": { label: "Explore energy-focused venues", to: "/?domain=D3%20Mitochondrial" },
  "Sleep": { label: "Explore sleep-supportive venues", to: "/?domain=D4%20Sleep" },
  "Stress": { label: "Explore stress-recovery venues", to: "/?domain=D5%20Stress%2FHPA" },
  "Gut Health": { label: "Explore gut-friendly venues", to: "/?domain=D6%20Gut" },
  "Heart Health": { label: "Explore heart-healthy venues", to: "/?domain=D7%20Cardiovascular" },
  "Immunity": { label: "Explore immunity-supportive venues", to: "/?domain=D8%20Immune" },
  "Brain Health": { label: "Explore brain-healthy venues", to: "/?domain=D9%20Neurocognitive" },
  "Movement & Recovery": { label: "Explore graded fitness venues", to: "/?category=fitness" },
  "Nutrition": { label: "Explore graded nutrition venues", to: "/?category=nutrition" },
};

export const formatHubDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export const TYPE_STYLES = {
  article: { label: "Article", cls: "bg-brand-blue/10 text-brand-blue" },
  video: { label: "Video", cls: "bg-indigo-50 text-indigo-600" },
  youtube: { label: "YouTube", cls: "bg-red-50 text-brand-red" },
  guide: { label: "Guide", cls: "bg-emerald-50 text-emerald-700" },
  event: { label: "Event", cls: "bg-amber-50 text-amber-700" },
};
